import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { updateProfile, getAuth, PhoneAuthProvider, linkWithCredential } from 'firebase/auth';
import { sanitizeTurkishPhone, formatPhoneInput } from '../lib/phoneUtils';
import { requestPhoneOtp, logPhoneAuthEvent } from '../lib/phoneOtp';
import { getPhoneAuthErrorMessage, isRecaptchaEnvironmentError } from '../lib/recaptchaUtils';
import BrowserCompatNotice from '../components/BrowserCompatNotice';

const UserProfilePage = () => {
    const { currentUser, userData, refreshUserData } = useAuth();
    const [displayName, setDisplayName] = useState(userData?.displayName || '');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');

    // Identity number state (masked, optional, read-only after save)
    const [idNumber, setIdNumber] = useState(userData?.idNumber ? '***********' : '');
    const [idSaved, setIdSaved] = useState(!!userData?.idNumber);
    const [idLoading, setIdLoading] = useState(false);
    const [idError, setIdError] = useState('');
    const [idSuccess, setIdSuccess] = useState('');

    // Phone verification state
    const [phone, setPhone] = useState(userData?.phone ? userData.phone : '');
    const [otp, setOtp] = useState('');
    const [phoneStep, setPhoneStep] = useState(1);
    const [phoneLoading, setPhoneLoading] = useState(false);
    const [phoneError, setPhoneError] = useState('');
    const [verificationId, setVerificationId] = useState(null);
    const [showBrowserTip, setShowBrowserTip] = useState(false);
    const [statusText, setStatusText] = useState('');
    const recaptchaHostRef = useRef(null);
    const sendingRef = useRef(false); // guards against double taps re-using a token

    const isPhoneVerified = !!userData?.isPhoneVerified;

    // ── Profile name save ──────────────────────────────────────
    const handleSave = async (e) => {
        e.preventDefault();
        if (displayName === userData.displayName) return;
        setLoading(true);
        setSuccess('');
        try {
            await updateProfile(currentUser, { displayName });
            const userDocRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userDocRef, { displayName });
            if (refreshUserData) await refreshUserData();
            setSuccess('Profiliniz başarıyla güncellendi.');
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error("Profile update error:", error);
        } finally {
            setLoading(false);
        }
    };

    // ── Identity number save ───────────────────────────────────
    const handleSaveId = async (e) => {
        e.preventDefault();
        const cleaned = idNumber.replace(/\D/g, '');
        if (cleaned.length !== 11) {
            setIdError('TC Kimlik No 11 haneli olmalıdır.');
            return;
        }
        const confirmed = window.confirm(
            'TC Kimlik Numaranız güvenli şekilde kaydedilecek ve daha sonra değiştirilemeyecektir. Devam etmek istiyor musunuz?'
        );
        if (!confirmed) return;

        setIdLoading(true);
        setIdError('');
        try {
            const userDocRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userDocRef, { idNumber: cleaned });
            if (refreshUserData) await refreshUserData();
            setIdSaved(true);
            setIdNumber('***********');
            setIdSuccess('TC Kimlik Numaranız güvenli şekilde kaydedildi.');
            setTimeout(() => setIdSuccess(''), 4000);
        } catch (err) {
            console.error("ID save error:", err);
            setIdError('Kayıt sırasında hata oluştu.');
        } finally {
            setIdLoading(false);
        }
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        if (sendingRef.current) return;

        const sanitized = sanitizeTurkishPhone(phone);
        if (!sanitized.isValid) {
            setPhoneError(sanitized.error);
            return;
        }

        sendingRef.current = true;
        setPhoneLoading(true);
        setPhoneError('');
        setStatusText('Güvenlik kontrolü yapılıyor...');
        try {
            const vId = await requestPhoneOtp({
                phoneNumber: sanitized.e164,
                host: recaptchaHostRef.current,
                onAttempt: (n) => setStatusText(n > 1 ? 'Tekrar deneniyor...' : 'Güvenlik kontrolü yapılıyor...'),
            });
            setVerificationId(vId);
            setShowBrowserTip(false);
            setPhoneError('');
            setPhoneStep(2);
        } catch (err) {
            console.error("Phone OTP error:", err);
            setPhoneError(getPhoneAuthErrorMessage(err));
            if (isRecaptchaEnvironmentError(err)) setShowBrowserTip(true);
        } finally {
            sendingRef.current = false;
            setStatusText('');
            setPhoneLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (!verificationId) { setPhoneError('Önce doğrulama kodu isteyin.'); return; }
        const sanitized = sanitizeTurkishPhone(phone);
        setPhoneLoading(true);
        setPhoneError('');
        try {
            const auth = getAuth();
            const credential = PhoneAuthProvider.credential(verificationId, otp.trim());
            try {
                await linkWithCredential(auth.currentUser, credential);
            } catch (linkErr) {
                if (linkErr.code === 'auth/provider-already-linked' || linkErr.code === 'auth/credential-already-in-use') {
                    console.warn("Credential already linked, proceeding to update user doc:", linkErr.code);
                } else {
                    throw linkErr;
                }
            }
            const userRef = doc(db, 'users', currentUser.uid);
            await updateDoc(userRef, { phone: sanitized.e164, isPhoneVerified: true });
            if (refreshUserData) await refreshUserData();
            setPhoneStep(1);
            setPhoneError('');
            setSuccess('Telefon numaranız başarıyla doğrulandı!');
            setTimeout(() => setSuccess(''), 5000);
        } catch (err) {
            console.error("OTP verify error:", err);
            logPhoneAuthEvent('otp_verify_error', { code: err?.code || 'unknown', message: String(err?.message || '') });
            if (err.code === 'auth/invalid-verification-code') {
                setPhoneError('Hatalı doğrulama kodu. Lütfen SMS ile gelen 6 haneli kodu kontrol ediniz.');
            } else if (err.code === 'auth/code-expired') {
                setPhoneError('Doğrulama kodunun süresi doldu. Lütfen "Numarayı Değiştir" ile yeni kod isteyin.');
            } else {
                setPhoneError(`Doğrulama başarısız: ${err.message || 'Lütfen tekrar deneyin.'}`);
            }
        } finally {
            setPhoneLoading(false);
        }
    };

    return (
        <div className="container mx-auto max-w-2xl p-4 sm:p-8 space-y-6">
            <h1 className="text-3xl font-extrabold tracking-tight">Profilim</h1>

            {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">
                    {success}
                </div>
            )}

            {/* ── Profile Info ─────────────────────────────────── */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h2 className="text-lg font-bold mb-4 text-gray-800">Profil Bilgileri</h2>
                <form onSubmit={handleSave} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Görünen Ad</label>
                        <input
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                        <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 px-3 py-2.5 rounded-lg">{currentUser.email}</p>
                    </div>
                    <button type="submit" disabled={loading}
                        className="w-full bg-black text-white py-2.5 rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors text-sm font-semibold">
                        {loading ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                </form>
            </div>

            {/* ── Phone Verification ───────────────────────────── */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-800">Telefon Doğrulama</h2>
                    {isPhoneVerified
                        ? <span className="text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">✓ Doğrulandı</span>
                        : <span className="text-xs font-semibold bg-yellow-100 text-yellow-700 px-2.5 py-1 rounded-full">Doğrulanmadı</span>
                    }
                </div>

                {isPhoneVerified ? (
                    <div className="text-sm text-gray-600">
                        <span className="font-medium">Kayıtlı numara:</span>{' '}
                        <span className="font-mono">{userData?.phone || '—'}</span>
                    </div>
                ) : (
                    <>
                        {phoneError && <p className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-3">{phoneError}</p>}
                        {phoneStep === 1 && <BrowserCompatNotice force={showBrowserTip} />}
                        {phoneStep === 1 ? (
                            <form onSubmit={handleSendOtp} className="space-y-3">
                                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                                    <span className="px-3 py-2.5 bg-gray-100 text-gray-600 text-sm font-medium border-r border-gray-300">+90</span>
                                    <input
                                        type="tel" value={phone}
                                        onChange={e => setPhone(formatPhoneInput(e.target.value))}
                                        placeholder="5XX XXX XX XX"
                                        className="flex-1 px-3 py-2.5 text-sm focus:outline-none"
                                    />
                                </div>
                                <button id="profile-send-otp-btn" type="submit" disabled={phoneLoading}
                                    className="w-full bg-black text-white py-2.5 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-semibold">
                                    {phoneLoading ? (statusText || 'Gönderiliyor...') : 'Doğrulama Kodu Gönder'}
                                </button>
                                {/* Invisible reCAPTCHA host (fresh widget is created per attempt) */}
                                <div ref={recaptchaHostRef} id="profile-recaptcha-container" className="flex justify-center"></div>
                                <p className="text-[10px] text-gray-400 text-center leading-snug">
                                    Bu form reCAPTCHA ile korunmaktadır. Google{' '}
                                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Gizlilik Politikası</a> ve{' '}
                                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Hizmet Şartları</a> geçerlidir.
                                </p>
                            </form>
                        ) : (
                            <form onSubmit={handleVerifyOtp} className="space-y-3">
                                <p className="text-sm text-gray-600 font-medium">{sanitizeTurkishPhone(phone).formatted || phone} numarasına gönderilen SMS kodunu girin:</p>
                                <input
                                    type="text" value={otp}
                                    onChange={e => setOtp(e.target.value)}
                                    placeholder="123456"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 tracking-widest text-center text-lg focus:outline-none focus:ring-2 focus:ring-black"
                                    maxLength={6} required
                                />
                                <button type="submit" disabled={phoneLoading}
                                    className="w-full bg-black text-white py-2.5 rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors text-sm font-semibold">
                                    {phoneLoading ? 'Doğrulanıyor...' : 'Doğrula'}
                                </button>
                                <button type="button"
                                    onClick={() => { setPhoneStep(1); setOtp(''); setPhoneError(''); setVerificationId(null); }}
                                    className="w-full text-sm text-gray-500 hover:text-black transition-colors">
                                    Numarayı Değiştir
                                </button>
                            </form>
                        )}
                    </>
                )}
            </div>

            {/* ── TC Kimlik No (Optional, Secure, Read-only after save) ── */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-1">
                    <h2 className="text-lg font-bold text-gray-800">TC Kimlik Numarası</h2>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">İsteğe Bağlı</span>
                </div>
                <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                    Kimlik bilginiz şifreli olarak saklanır ve yalnızca yetkililer tarafından görüntülenebilir.
                    Kaydedildikten sonra değiştirilemez.
                </p>

                {idSuccess && <p className="bg-green-50 text-green-700 p-3 rounded-lg text-sm mb-3">{idSuccess}</p>}
                {idError && <p className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-3">{idError}</p>}

                {idSaved ? (
                    <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 px-4 py-3 rounded-lg">
                        <span className="text-lg">🔒</span>
                        <div>
                            <p className="text-sm font-medium text-gray-700">Kimlik numarası kayıtlı</p>
                            <p className="text-xs text-gray-500 font-mono tracking-widest">***  ****  ****</p>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSaveId} className="space-y-3">
                        <input
                            type="text"
                            inputMode="numeric"
                            value={idNumber}
                            onChange={e => setIdNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
                            placeholder="Kimlik numaranızı girin"
                            maxLength={11}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 tracking-widest text-sm focus:outline-none focus:ring-2 focus:ring-black"
                        />
                        <button type="submit" disabled={idLoading}
                            className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white py-2.5 rounded-lg hover:bg-black disabled:opacity-50 transition-colors text-sm font-semibold">
                            🔒 {idLoading ? 'Kaydediliyor...' : 'Güvenli Kaydet'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default UserProfilePage;